/**
 * Tool form component — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui Switch in ui-form-choice-field CSS classes
 */

// React Imports
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Wrench, Settings, Code, Shield } from 'lucide-react';

// @coordio/ui Imports
import { CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

// Component Imports
import { PageFormLayout } from '../layouts/PageFormLayout';
import { CodeEditor } from '../common/CodeEditor';
import { toolService } from '../../services/toolService';
import { useNotificationStore } from '../../store/notificationStore';

// Types
import { ToolExecutionLocation, type ToolDetailDto, type ToolRiskLevel } from '../../types/tool';

const riskLevelToString = (risk: ToolRiskLevel): string => {
  switch (risk) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    case 3:
      return 'Critical';
    default:
      return 'Low';
  }
};

const riskLevelOptions = [
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' },
  { label: 'Critical', value: 'Critical' },
];

const executionLocationOptions = [
  { label: 'Server Only', value: String(ToolExecutionLocation.Server) },
  { label: 'Client Only', value: String(ToolExecutionLocation.Client) },
  { label: 'Both', value: String(ToolExecutionLocation.Both) },
];

const implementationTypeOptions = [
  { label: 'CSharp', value: 'CSharp' },
  { label: 'REST', value: 'REST' },
  { label: 'RPA', value: 'RPA' },
  { label: 'MCP', value: 'MCP' },
  { label: 'Shell (server host)', value: 'Shell' },
];

/**
 * Tool form component for creating and editing tools.
 */
export const ToolForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [_tool, setTool] = useState<ToolDetailDto | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [riskLevel, setRiskLevel] = useState<string>('Low');
  const [executionLocation, setExecutionLocation] = useState<ToolExecutionLocation>(ToolExecutionLocation.Server);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [version, setVersion] = useState('1.0.0');
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [implementationType, setImplementationType] = useState<string>('CSharp');
  const [inputSchemaJson, setInputSchemaJson] = useState<string>('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState<string>('{}');
  const [implementationJson, setImplementationJson] = useState<string>('{}');
  const [exampleUsageJson, setExampleUsageJson] = useState<string>('{}');
  const [metadataJson, setMetadataJson] = useState<string>('{}');

  const title = useMemo(() => (isEdit ? 'Edit Tool' : 'Create Tool'), [isEdit]);

  useEffect(() => {
    if (isEdit && id) {
      loadTool();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadTool = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await toolService.getTool(id);
      setTool(data);
      setName(data.name);
      setDisplayName(data.displayName);
      setDescription(data.description || '');
      setCategory(data.category || '');
      setRiskLevel(riskLevelToString(data.riskLevel));
      setExecutionLocation(
        data.executionLocation === ToolExecutionLocation.Client || data.executionLocation === ToolExecutionLocation.Both
          ? data.executionLocation
          : ToolExecutionLocation.Server
      );
      setRequiresApproval(!!data.requiresApproval);
      setIsDefault(!!data.isDefault);
      setIsActive(!!data.isActive);
      setVersion(data.version || '1.0.0');
      setDocumentationUrl(data.documentationUrl || '');
      setImplementationType(data.implementationType || 'CSharp');
      setInputSchemaJson(data.inputSchemaJson || '{}');
      setOutputSchemaJson(data.outputSchemaJson || '{}');
      setImplementationJson(data.implementationJson || '{}');
      setExampleUsageJson(data.exampleUsageJson || '{}');
      setMetadataJson(data.metadataJson || '{}');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load tool';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate name from displayName for new tools (basic, safe slug-style)
  useEffect(() => {
    if (!isEdit && !nameTouched) {
      const generated = displayName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '.')
        .replace(/^\.+|\.+$/g, '');
      if (generated !== name) setName(generated);
    }
  }, [displayName, isEdit, name, nameTouched]);

  const handleSubmit = async () => {
    setError(null);

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!category.trim()) {
      setError('Category is required');
      return;
    }

    if (!isEdit && !name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await toolService.updateTool(id, {
          displayName: displayName.trim(),
          description: description.trim(),
          category: category.trim(),
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          isDefault,
          isActive,
          version: version || '1.0.0',
          requiresApproval,
          riskLevel,
          executionLocation,
          implementationType: implementationType || undefined,
          implementationJson: implementationJson || undefined,
          documentationUrl: documentationUrl || undefined,
          exampleUsageJson: exampleUsageJson || undefined,
          metadataJson: metadataJson || undefined,
        });
        addNotification('Tool updated successfully', 'success');
        navigate(`/tools/${id}`);
      } else {
        const created = await toolService.createTool({
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim(),
          category: category.trim(),
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          isDefault,
          isActive,
          isSystem: false,
          version: version || '1.0.0',
          requiresApproval,
          riskLevel,
          executionLocation,
          implementationType: implementationType || undefined,
          implementationJson: implementationJson || undefined,
          documentationUrl: documentationUrl || undefined,
          exampleUsageJson: exampleUsageJson || undefined,
          metadataJson: metadataJson || undefined,
        });
        addNotification('Tool created successfully', 'success');
        navigate(`/tools/${created.id}`);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save tool';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={title}
      subtitle="Configure tool identity, execution parameters, implementation schemas, and security controls."
      data-testid="tool-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: isEdit && id ? `/tools/${id}` : '/tools' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate(isEdit && id ? `/tools/${id}` : '/tools'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Wrench size={16} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'execution', label: 'Execution', icon: <Settings size={16} /> },
            { id: 'implementation', label: 'Implementation', icon: <Code size={16} /> },
            { id: 'security', label: 'Security', icon: <Shield size={16} /> },
          ],
        },
      ]}
      sections={[
        /* ── Details ─────────────────────────────────────────────── */
        {
          id: 'details',
          title: 'Details',
          description: 'Tool name, display name, category, version, and description.',
          icon: <Wrench size={20} />,
          content: (
            <FormSection
              title="Tool identity"
              description="The name is a stable machine identifier and cannot be changed after creation."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                {!isEdit && (
                  <FormField
                    label="Name"
                    required
                    description="Auto-generated from display name. Override to customise."
                    data-testid="tool-name"
                  >
                    <Input
                      className="w-full"
                      value={name}
                      onChange={(e) => {
                        setNameTouched(true);
                        setName(e.target.value);
                      }}
                      data-testid="tool-name"
                    />
                  </FormField>
                )}

                <FormField
                  label="Display Name"
                  required
                  description="Human-readable name shown in the UI."
                  data-testid="tool-displayName"
                >
                  <Input
                    className="w-full"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    data-testid="tool-displayName"
                  />
                </FormField>

                <FormField
                  label="Category"
                  required
                  description="Logical grouping for the tool (e.g. data, communication)."
                  data-testid="tool-category"
                >
                  <Input
                    className="w-full"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    required
                    data-testid="tool-category"
                  />
                </FormField>

                <FormField
                  label="Version"
                  description="Semantic version of this tool definition."
                  data-testid="tool-version"
                >
                  <Input
                    className="w-full"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    data-testid="tool-version"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Brief summary of what this tool does."
                  data-testid="tool-description"
                >
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="tool-description"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },

        /* ── Execution ───────────────────────────────────────────── */
        {
          id: 'execution',
          title: 'Execution',
          description: 'Risk level, execution location, implementation type, and documentation.',
          icon: <Settings size={20} />,
          content: (
            <FormSection
              title="Runtime parameters"
              description="Control where the tool runs and how it is categorised for risk."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Risk Level"
                  description="Severity classification for this tool's operations."
                  data-testid="tool-riskLevel"
                >
                  <CoordioDropdown
                    options={riskLevelOptions}
                    value={riskLevel}
                    onChange={(val) => setRiskLevel(val ?? 'Low')}
                    clearable={false}
                    data-testid="tool-riskLevel"
                  />
                </FormField>

                <FormField
                  label="Runs on"
                  description="Where this tool executes: server, client, or both."
                  data-testid="tool-executionLocation"
                >
                  <CoordioDropdown
                    options={executionLocationOptions}
                    value={String(executionLocation)}
                    onChange={(val) => setExecutionLocation(Number(val ?? ToolExecutionLocation.Server) as ToolExecutionLocation)}
                    clearable={false}
                    data-testid="tool-executionLocation"
                  />
                </FormField>

                <FormField
                  label="Implementation Type"
                  description="Technology used to execute this tool."
                  data-testid="tool-implementationType"
                >
                  <CoordioDropdown
                    options={implementationTypeOptions}
                    value={implementationType}
                    onChange={(val) => setImplementationType(val ?? 'CSharp')}
                    clearable={false}
                    data-testid="tool-implementationType"
                  />
                </FormField>

                <FormField
                  label="Documentation URL"
                  description="Link to external documentation for this tool."
                  data-testid="tool-documentationUrl"
                >
                  <Input
                    className="w-full"
                    value={documentationUrl}
                    onChange={(e) => setDocumentationUrl(e.target.value)}
                    data-testid="tool-documentationUrl"
                  />
                </FormField>
              </div>

              <div className="mt-5 rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm dark:border-blue-700 dark:bg-blue-950">
                <div className="mb-3">
                  <strong>Local machine (user / developer PC):</strong> set <strong>Runs on: Client Only</strong> or <strong>Both</strong>{' '}
                  and do <strong>not</strong> use <strong>Shell</strong> as the implementation type. Keep something like{' '}
                  <strong>CSharp</strong> for this catalog row; run the real command in your <strong>AgentOps.Client</strong> app by
                  registering a tool with the <strong>same name and JSON schemas</strong> (for example <code>AgentOps.Client.Tools.RunShellTool</code>).
                </div>
                <div>
                  <strong>API server:</strong> pick <strong>Shell</strong> only if the process must run on the host where the API runs.
                  Then enable <strong>ShellExecution</strong> in API appsettings and use <strong>Runs on: Server Only</strong> unless you
                  deliberately use hybrid routing. For attach-system-tool, use <strong>Default tool</strong> (or system via API).
                </div>
              </div>
            </FormSection>
          ),
        },

        /* ── Implementation ──────────────────────────────────────── */
        {
          id: 'implementation',
          title: 'Implementation',
          description: 'JSON schemas for input/output validation, implementation details, and metadata.',
          icon: <Code size={20} />,
          content: (
            <FormSection
              title="Schemas & configuration"
              description="Provide JSON schemas for input and output validation, implementation details, and optional metadata."
            >
              <FormField
                label="Input Schema (JSON)"
                description="JSON Schema defining the tool's input parameters."
                data-testid="tool-inputSchema"
              >
                <CodeEditor value={inputSchemaJson} onChange={setInputSchemaJson} language="json" height="220px" />
              </FormField>

              <div className="mt-5">
                <FormField
                  label="Output Schema (JSON)"
                  description="JSON Schema defining the tool's output format."
                  data-testid="tool-outputSchema"
                >
                  <CodeEditor value={outputSchemaJson} onChange={setOutputSchemaJson} language="json" height="220px" />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Implementation Details (JSON)"
                  description={
                    implementationType === 'Shell'
                      ? 'Not used for Shell — leave as {}. Command parameters come from the input JSON schema.'
                      : 'Optional configuration for REST, RPA, MCP, etc.'
                  }
                  data-testid="tool-implementationDetails"
                >
                  <CodeEditor value={implementationJson} onChange={setImplementationJson} language="json" height="200px" />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Example Usage (JSON)"
                  description="Sample invocation payload for documentation."
                  data-testid="tool-exampleUsage"
                >
                  <CodeEditor value={exampleUsageJson} onChange={setExampleUsageJson} language="json" height="160px" />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Metadata (JSON)"
                  description="Arbitrary key-value metadata for this tool."
                  data-testid="tool-metadata"
                >
                  <CodeEditor value={metadataJson} onChange={setMetadataJson} language="json" height="160px" />
                </FormField>
              </div>
            </FormSection>
          ),
        },

        /* ── Security ────────────────────────────────────────────── */
        {
          id: 'security',
          title: 'Security',
          description: 'Approval, default assignment, and activation controls.',
          icon: <Shield size={20} />,
          content: (
            <FormSection
              title="Access controls"
              description="Configure approval requirements, default assignment, and whether the tool is active."
            >
              <div className="flex flex-col gap-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={requiresApproval}
                        onCheckedChange={(checked) => setRequiresApproval(checked)}
                        data-testid="tool-requiresApproval"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Requires Approval</span>
                    </div>
                  </div>
                </div>

                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isDefault}
                        onCheckedChange={(checked) => setIsDefault(checked)}
                        data-testid="tool-isDefault"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Default Tool</span>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                        Automatically assigned to new agents
                      </p>
                    </div>
                  </div>
                </div>

                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={(checked) => setIsActive(checked)}
                        data-testid="tool-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
